import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Clock, Users, ArrowRight, ArrowLeft, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import LazyImage from "@/components/LazyImage";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industries = ["All", "IT & Technology", "BFSI", "Co-working", "BPO / Call Centre", "Manufacturing"];

// Fallback data - always available even if database is empty
const DEFAULT_CASE_STUDIES = [
  {
    id: "1",
    title: "TechCorp IT Services - Mumbai Campus Expansion",
    slug: "it-services-expansion",
    industry: "IT & Technology",
    location: "Mumbai, Maharashtra",
    scope: "350 workstations across 3 floors with collaboration zones and meeting rooms",
    challenge: "TechCorp needed to expand their Mumbai campus to accommodate 350 new engineers while maintaining their open, collaborative culture. The existing furniture vendors couldn't meet their 6-week deadline.",
    solution: "We deployed our rapid-installation modular workstation system with integrated cable management. Custom collaboration pods were designed for agile team meetings.",
    outcome: "Project completed in 5.5 weeks, under budget. Employee satisfaction scores increased by 23% post-move.",
    timeline: "6 weeks",
    images: [
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/vbkl67zd_board%20room.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/nd141fbg_Breakout.PNG"
    ],
    workstations: 350
  },
  {
    id: "2",
    title: "National Bank HQ - Executive Floor Renovation",
    slug: "banking-hq-renovation",
    industry: "BFSI",
    location: "Delhi NCR",
    scope: "Complete executive floor redesign including 12 private offices and boardroom",
    challenge: "National Bank required a prestigious executive floor that reflected their 50-year legacy while incorporating modern amenities. Security and privacy were paramount.",
    solution: "Premium veneer workstations with integrated biometric locks. Custom boardroom table seating 24 with built-in AV systems. Acoustic panels for complete sound privacy.",
    outcome: "The renovated floor has hosted 3 international investor meetings. Featured in Banking Today magazine.",
    timeline: "8 weeks",
    images: [
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/aq3gcgmc_Capturea.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/xvy56tpb_Captureb.PNG"
    ],
    workstations: 120
  },
  {
    id: "3",
    title: "WorkHub - Premium Co-working Space Launch",
    slug: "coworking-space-setup",
    industry: "Co-working",
    location: "Bangalore, Karnataka",
    scope: "450-seat co-working facility with hot desks, private offices, and event space",
    challenge: "WorkHub needed furniture that could withstand heavy daily use while maintaining a premium aesthetic. Flexibility was key - spaces needed to transform for events.",
    solution: "Modular furniture system allowing quick reconfiguration. Heavy-duty yet elegant workstations. Stackable seating for event space. Phone booths for privacy.",
    outcome: "95% occupancy within 3 months of launch. The space has been featured as one of Bangalore's top co-working destinations.",
    timeline: "10 weeks",
    images: [
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/c0b8v32j_meeting%20zone.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/jaeuaysw_informal%20seatout%202.PNG"
    ],
    workstations: 450
  },
  {
    id: "4",
    title: "GlobalConnect BPO - 24/7 Operations Center",
    slug: "bpo-operations-center",
    industry: "BPO / Call Centre",
    location: "Hyderabad, Telangana",
    scope: "800 workstations with 24/7 operations capability, training center, and break rooms",
    challenge: "GlobalConnect's new delivery center needed to support round-the-clock operations with three shifts. Acoustic management was critical as voice-based processes required clear audio.",
    solution: "High-density workstations with integrated acoustic panels reducing ambient noise by 40%. Ergonomic seating rated for 24/7 use with 5-year warranty.",
    outcome: "Zero furniture-related complaints in the first year. Operator turnover reduced versus industry average.",
    timeline: "12 weeks",
    images: [
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/ruwxkw0r_reception%202.png",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/8mmt24wa_playzone.PNG"
    ],
    workstations: 800
  },
  {
    id: "5",
    title: "SteelWorks Industries - Corporate Office",
    slug: "manufacturing-corporate-office",
    industry: "Manufacturing",
    location: "Ahmedabad, Gujarat",
    scope: "200 workstations, training center, product showcase area, and visitor center",
    challenge: "SteelWorks wanted their corporate office to reflect their manufacturing heritage while providing modern corporate amenities.",
    solution: "Custom furniture incorporating exposed metal elements and industrial finishes reflecting the company's product line. Flexible training room accommodates classroom, workshop, and presentation configurations.",
    outcome: "The office became a showcase for client visits, with the visitor center contributing to a 30% increase in dealer sign-ups.",
    timeline: "9 weeks",
    images: [
      "https://customer-assets.emergentagent.com/job_c7ca78d1-ab7b-4712-8b24-d64ce9919df7/artifacts/ldlf27ml_Capture%201.PNG",
      "https://customer-assets.emergentagent.com/job_c7ca78d1-ab7b-4712-8b24-d64ce9919df7/artifacts/5v0vhn5o_Capture%202.PNG"
    ],
    workstations: 200
  }
];

export default function CaseStudiesPage() {
  const [caseStudies, setCaseStudies] = useState(DEFAULT_CASE_STUDIES);
  const [filteredStudies, setFilteredStudies] = useState(DEFAULT_CASE_STUDIES);
  const [selectedIndustry, setSelectedIndustry] = useState("All");
  const [loading, setLoading] = useState(true);

  useCanonical("https://ezeefitofficesystems.in/case-studies");
  useMetaDescription("Ezeefit corporate office furniture case studies — turnkey projects for IT companies, banks, BPOs & public sector across Mumbai, Navi Mumbai, Thane.");

  useEffect(() => {
    document.title = "Corporate Office Furniture Case Studies Mumbai | Project Portfolio | Ezeefit";
  }, []);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await axios.get(`${API}/case-studies`);
        if (response.data && response.data.length > 0) {
          setCaseStudies(response.data);
          setFilteredStudies(response.data);
        }
        // If API returns empty, we already have DEFAULT_CASE_STUDIES loaded
      } catch (err) {
        console.error("Failed to fetch case studies, using defaults:", err);
        // Keep using DEFAULT_CASE_STUDIES which is already set
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, []);

  useEffect(() => {
    if (selectedIndustry === "All") {
      setFilteredStudies(caseStudies);
    } else {
      setFilteredStudies(
        caseStudies.filter((study) => study.industry === selectedIndustry)
      );
    }
  }, [selectedIndustry, caseStudies]);

  return (
    <div className="pt-20" data-testid="case-studies-page">
      <Helmet>
        <meta name="description" content="Ezeefit corporate office furniture case studies — turnkey projects for IT companies, banks, BPOs & public sector across Mumbai, Navi Mumbai, Thane." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/case-studies" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({"@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{"@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in"}, {"@type": "ListItem", "position": 2, "name": "Case Studies", "item": "https://ezeefitofficesystems.in/case-studies"}]}) }} />
      {/* Hero */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Link>
            
            <div className="text-center">
              <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
                Our Work
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Project Case Studies
              </h1>
              <p className="text-lg text-[#CBD5E1] max-w-3xl mx-auto mb-4">
                At Ezeefit Office Systems, we believe no two workspaces should be the same.
              </p>
              <p className="text-[#94A3B8] max-w-3xl mx-auto mb-3">
                The projects showcased here represent only a glimpse of our capabilities. Most of our furniture solutions are custom-designed to reflect the client's vision, workspace layout, and functional requirements.
              </p>
              <p className="text-[#94A3B8] max-w-3xl mx-auto italic">
                Each product we deliver is therefore not just furniture, but a carefully engineered solution created specifically for that environment.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-4 overflow-x-auto pb-2">
            <Filter className="w-5 h-5 text-[#64748B] shrink-0" />
            {industries.map((industry) => (
              <button
                key={industry}
                onClick={() => setSelectedIndustry(industry)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedIndustry === industry
                    ? "bg-[#0F172A] text-white"
                    : "bg-[#F1F5F9] text-[#334155] hover:bg-[#E2E8F0]"
                }`}
                data-testid={`filter-${industry.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {industry}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full mx-auto" />
              <p className="text-[#64748B] mt-4">Loading case studies...</p>
            </div>
          ) : filteredStudies.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#64748B]">No case studies found for this industry.</p>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-8">
              {filteredStudies.map((study, index) => (
                <motion.div
                  key={study.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white border border-[#E2E8F0] card-hover group w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)]"
                >
                  <Link
                    to={`/case-studies/${study.slug}`}
                    className="block"
                    data-testid={`case-study-card-${study.slug}`}
                  >
                    <div className="relative overflow-hidden card-image-zoom">
                      <div className="aspect-[16/10]">
                        <LazyImage
                          src={study.images?.[0] || "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1280&q=70"}
                          alt={`${study.title} - Office furniture project case study by Ezeefit Mumbai`}
                          containerClassName="w-full h-full"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute top-4 left-4">
                        <span className="bg-[#F97316] text-white text-xs font-mono px-3 py-1 uppercase tracking-wider">
                          {study.industry}
                        </span>
                      </div>
                    </div>

                    <div className="p-6">
                      <h3 className="text-xl font-bold text-[#0F172A] mb-3 group-hover:text-[#F97316] transition-colors">
                        {study.title}
                      </h3>
                      
                      <div className="flex flex-wrap gap-4 text-sm mb-4">
                        <div className="flex items-center gap-2 text-[#64748B]">
                          <MapPin className="w-4 h-4" />
                          {study.location}
                        </div>
                        <div className="flex items-center gap-2 text-[#64748B]">
                          <Users className="w-4 h-4" />
                          {study.workstations} seats
                        </div>
                        <div className="flex items-center gap-2 text-[#64748B]">
                          <Clock className="w-4 h-4" />
                          {study.timeline}
                        </div>
                      </div>

                      <p className="text-[#64748B] text-sm line-clamp-2 mb-4">
                        {study.challenge}
                      </p>

                      <div className="flex items-center gap-2 text-[#F97316] font-semibold text-sm">
                        View Case Study
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Want Your Project Featured Here?
          </h2>
          <p className="text-lg text-[#94A3B8] mb-8">
            Let's create a success story together. Explore our{" "}
            <Link to="/manufacturing" className="text-[#F97316] underline hover:text-orange-400">manufacturing capabilities</Link>{" "}
            and{" "}
            <Link to="/process" className="text-[#F97316] underline hover:text-orange-400">proven execution process</Link>.
          </p>
          <Button
            asChild
            className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-10 py-6 font-semibold uppercase tracking-wider"
          >
            <Link to="/contact">
              Start Your Project
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      <ExploreMore currentPage="caseStudies" />
    </div>
  );
}
